<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import type { Product } from "@/types";
import { useUpdateProduct, useDeleteProduct } from "@/queries/products";
import { useAlertStore } from "@/stores/alerts-store";
import FormAlert from "@/components/FormAlert.vue";
import DeleteProductModal from "../modals/DeleteProductModal.vue";

interface Props {
  product: Product | null;
  productStatus: any;
  masterProjectName?: string;
}

const props = defineProps<Props>();

const router = useRouter();
const alertStore = useAlertStore();

const { updateProduct, updateProductPending, updateProductError, updateProductReset } = useUpdateProduct();
const { deleteProductAsync, deleteProductPending } = useDeleteProduct();

// Delete is gated on active subscribers — the entitlement the publisher controls. subscriberCount is
// derived from the published version's snapshot (0 when unpublished), so an unpublished product is
// freely deletable. The backend re-checks and returns 409 on a race, surfaced as an error toast.
const activeSubscriberCount = computed<number>(() => props.productStatus?.subscriberCount ?? 0);
const canDelete = computed(() => activeSubscriberCount.value === 0);

const showDeleteModal = ref(false);

async function confirmDelete() {
  if (!props.product) return;
  try {
    await deleteProductAsync(props.product.id);
    showDeleteModal.value = false;
    alertStore.addSuccess("Product deleted", `${props.product.name} has been removed.`);
    router.push("/publisher/products");
  } catch (err: any) {
    showDeleteModal.value = false;
    alertStore.addError(
      err?.problem?.title ?? "Delete failed",
      err?.userMessage ?? "The product could not be deleted.",
    );
  }
}

const productSettings = ref({
  name: "",
  description: "",
  category: "Marketing Automation",
  visibility: "public",
  autoUpdate: true,
  maxSubscribers: 100,
  pricing: {
    model: "per-subscriber",
    basePrice: 29.99,
    currency: "USD",
  },
});

const notificationSettings = ref({
  emailOnNewSubscriber: true,
  emailOnError: true,
  emailOnVersionPublish: false,
  webhookUrl: "https://api.example.com/webhooks/publisher",
});

const accessSettings = ref({
  requireApproval: false,
  allowTrials: true,
  trialDuration: 14,
  apiAccess: true,
});

const saveSuccess = ref(false);

watch(
  () => props.product,
  (p) => {
    if (p) {
      productSettings.value.name = p.name;
      productSettings.value.description = p.description;
    }
  },
  { immediate: true }
);

const isDirty = computed(() => {
  if (!props.product) return false;
  return (
    productSettings.value.name !== props.product.name ||
    productSettings.value.description !== props.product.description
  );
});

function saveChanges() {
  if (!props.product) return;
  saveSuccess.value = false;
  updateProductReset();
  updateProduct(
    {
      id: props.product.id,
      name: productSettings.value.name.trim(),
      description: productSettings.value.description.trim(),
    },
    {
      onSuccess: () => {
        saveSuccess.value = true;
      },
    },
  );
}
</script>

<template>
  <div class="settings-tab">
    <!-- Product Information -->
    <div class="card mb-4">
      <div class="card-body">
        <h2 class="card-title mb-3">Product Information</h2>

        <FormAlert :error="updateProductError" @dismiss="updateProductReset" />
        <div v-if="saveSuccess" class="alert alert-success alert-dismissible fade show" role="alert">
          <i class="bi bi-check-circle me-2"></i>Changes saved.
          <button type="button" class="btn-close" @click="saveSuccess = false"></button>
        </div>

        <div class="row mb-3">
          <label class="col-sm-3 col-form-label">Product Name</label>
          <div class="col-sm-9">
            <input type="text" class="form-control" v-model="productSettings.name" :disabled="updateProductPending" />
          </div>
        </div>
        <fieldset disabled class="opacity-50">
          <div class="row mb-3">
            <label class="col-sm-3 col-form-label">Category</label>
            <div class="col-sm-9">
              <select class="form-select" v-model="productSettings.category">
                <option value="Marketing Automation">Marketing Automation</option>
                <option value="CRM Integration">CRM Integration</option>
                <option value="Analytics">Analytics</option>
                <option value="Data Processing">Data Processing</option>
              </select>
            </div>
          </div>
        </fieldset>
        <div class="row mb-3">
          <label class="col-sm-3 col-form-label">Description</label>
          <div class="col-sm-9">
            <textarea class="form-control" rows="3" v-model="productSettings.description" :disabled="updateProductPending"></textarea>
          </div>
        </div>

        <div class="d-flex justify-content-end">
          <button
            type="button"
            class="bb-btn-primary-cta"
            :disabled="!isDirty || updateProductPending"
            @click="saveChanges"
          >
            <span v-if="updateProductPending" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <i v-else class="bi bi-check me-1"></i>
            {{ updateProductPending ? "Saving..." : "Save Changes" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Coming Soon Banner -->
    <div class="alert alert-info d-flex align-items-center" role="alert">
      <i class="bi bi-info-circle me-2"></i>
      <span>Additional settings are coming soon. The fields shown below are illustrative only.</span>
    </div>

    <fieldset disabled class="opacity-50">
      <!-- Additional Product Settings -->
      <div class="card mb-4">
        <div class="card-body">
          <h2 class="card-title mb-3">Additional Settings</h2>
          <div class="row mb-3">
            <label class="col-sm-3 col-form-label">Visibility</label>
            <div class="col-sm-9">
              <select class="form-select" v-model="productSettings.visibility">
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="unlisted">Unlisted</option>
              </select>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-sm-3 col-form-label">Max Subscribers</label>
            <div class="col-sm-9">
              <input type="number" class="form-control" v-model="productSettings.maxSubscribers" />
            </div>
          </div>
        </div>
      </div>

      <!-- Pricing Settings -->
      <div class="card mb-4">
        <div class="card-body">
          <h2 class="card-title mb-3">Pricing Settings</h2>
          <div class="row mb-3">
            <label class="col-sm-3 col-form-label">Pricing Model</label>
            <div class="col-sm-9">
              <select class="form-select" v-model="productSettings.pricing.model">
                <option value="per-subscriber">Per Subscriber</option>
                <option value="flat-rate">Flat Rate</option>
                <option value="usage-based">Usage Based</option>
                <option value="free">Free</option>
              </select>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-sm-3 col-form-label">Base Price</label>
            <div class="col-sm-9">
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input type="number" class="form-control" step="0.01" v-model="productSettings.pricing.basePrice" />
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-sm-3 col-form-label">Currency</label>
            <div class="col-sm-9">
              <select class="form-select" v-model="productSettings.pricing.currency">
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Notification Settings -->
      <div class="card mb-4">
        <div class="card-body">
          <h2 class="card-title mb-3">Notification Settings</h2>
          <div class="row mb-3">
            <label class="col-sm-3 col-form-label">Email Notifications</label>
            <div class="col-sm-9">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="notifyNewSubscriber" v-model="notificationSettings.emailOnNewSubscriber" />
                <label class="form-check-label" for="notifyNewSubscriber">Notify on new subscriber</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="notifyError" v-model="notificationSettings.emailOnError" />
                <label class="form-check-label" for="notifyError">Notify on errors</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="notifyVersionPublish" v-model="notificationSettings.emailOnVersionPublish" />
                <label class="form-check-label" for="notifyVersionPublish">Notify on version publish</label>
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-sm-3 col-form-label">Webhook URL</label>
            <div class="col-sm-9">
              <input type="url" class="form-control" v-model="notificationSettings.webhookUrl" placeholder="https://api.example.com/webhooks" />
              <div class="form-text">Receive notifications via webhook for integration with your systems</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Access Settings -->
      <div class="card mb-4">
        <div class="card-body">
          <h2 class="card-title mb-3">Access Settings</h2>
          <div class="row mb-3">
            <div class="col-sm-9 offset-sm-3">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="requireApproval" v-model="accessSettings.requireApproval" />
                <label class="form-check-label" for="requireApproval">Require approval for new subscribers</label>
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-sm-9 offset-sm-3">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="allowTrials" v-model="accessSettings.allowTrials" />
                <label class="form-check-label" for="allowTrials">Allow trial subscriptions</label>
              </div>
            </div>
          </div>
          <div class="row mb-3" v-if="accessSettings.allowTrials">
            <label class="col-sm-3 col-form-label">Trial Duration (days)</label>
            <div class="col-sm-9">
              <input type="number" class="form-control" v-model="accessSettings.trialDuration" />
            </div>
          </div>
          <div class="row mb-3">
            <div class="col-sm-9 offset-sm-3">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="apiAccess" v-model="accessSettings.apiAccess" />
                <label class="form-check-label" for="apiAccess">Enable API access for subscribers</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Advanced Settings -->
      <div class="card mb-4">
        <div class="card-body">
          <h2 class="card-title mb-3">Advanced Settings</h2>
          <div class="row mb-3">
            <div class="col-sm-9 offset-sm-3">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="autoUpdate" v-model="productSettings.autoUpdate" />
                <label class="form-check-label" for="autoUpdate">Auto-update subscribers to latest version</label>
                <div class="form-text">When enabled, all subscribers will automatically receive updates when you publish a new version</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons (illustrative) -->
      <div class="d-flex justify-content-end gap-2">
        <button class="bb-btn-secondary-cta" type="button">
          <i class="bi bi-arrow-clockwise me-1"></i> Reset to Defaults
        </button>
        <button class="bb-btn-primary-cta" type="button">
          <i class="bi bi-check me-1"></i> Save Changes
        </button>
      </div>
    </fieldset>

    <!-- Danger Zone — real, enabled action (outside the illustrative fieldset above) -->
    <div class="card border-danger mb-4">
      <div class="card-body">
        <h2 class="card-title text-danger mb-3">Danger Zone</h2>
        <div class="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div class="fw-semibold">Delete this product</div>
            <div class="text-muted small">
              Permanently removes the product and moves its master project to Deleted (restorable).
            </div>
            <div v-if="!canDelete" class="text-warning-emphasis small mt-1">
              <i class="bi bi-exclamation-circle me-1"></i>
              {{ activeSubscriberCount }}
              {{ activeSubscriberCount === 1 ? 'organisation' : 'organisations' }}
              still {{ activeSubscriberCount === 1 ? 'has' : 'have' }} access. Remove their
              subscriptions on the Subscribers tab before deleting.
            </div>
          </div>
          <button
            class="bb-btn-danger"
            type="button"
            :disabled="!product || !canDelete || deleteProductPending"
            @click="showDeleteModal = true"
          >
            <i class="bi bi-trash me-1"></i> Delete Product
          </button>
        </div>
      </div>
    </div>

    <DeleteProductModal
      :show="showDeleteModal"
      :product-name="product?.name ?? ''"
      :master-project-name="masterProjectName ?? 'its master project'"
      :pending="deleteProductPending"
      @confirm="confirmDelete"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<style scoped lang="scss">
/* Component styles inherited from parent */
</style>
